import type { Pool, RowDataPacket } from "mysql2/promise";

export const DEFAULT_SOCIETY_NAME = "Elenails & Hair";
export const DEFAULT_SITE_DESCRIPTION = "Prestations d’ongles et de tresses sur rendez-vous à Wissembourg.";
export const DEFAULT_SITE_CITY = "Wissembourg";
export const DEFAULT_SITE_LOCATION_URL = "https://www.google.com/maps/search/?api=1&query=Wissembourg+Gare";
export const DEFAULT_SITE_INSTAGRAM = process.env.NEXT_PUBLIC_INSTAGRAM_URL || "https://www.instagram.com/elenailshair_67/";
export const DEFAULT_SITE_FACEBOOK = process.env.NEXT_PUBLIC_FACEBOOK_URL || "https://www.facebook.com/people/EK-Hairnails/61575100672787/";
export const DEFAULT_SITE_WHATSAPP = process.env.NEXT_PUBLIC_WHATSAPP_URL || "";

export type SiteSettings = {
  societyName: string;
  description: string;
  city: string;
  locationUrl: string;
  instagram: string;
  facebook: string;
  whatsapp: string;
};

type SiteSettingsRow = RowDataPacket & {
  society_name: string | null;
  description: string | null;
  city: string | null;
  location_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  whatsapp_url: string | null;
};

export function defaultSiteSettings(): SiteSettings {
  return {
    societyName: DEFAULT_SOCIETY_NAME,
    description: DEFAULT_SITE_DESCRIPTION,
    city: DEFAULT_SITE_CITY,
    locationUrl: DEFAULT_SITE_LOCATION_URL,
    instagram: DEFAULT_SITE_INSTAGRAM,
    facebook: DEFAULT_SITE_FACEBOOK,
    whatsapp: DEFAULT_SITE_WHATSAPP,
  };
}

function text(value: unknown, length: number) {
  return typeof value === "string" ? value.trim().slice(0, length) : "";
}

function optionalText(value: unknown, fallback: string, length: number) {
  return typeof value === "string" ? value.trim().slice(0, length) : fallback;
}

export function normalizeSiteSettings(value: unknown, fallback = defaultSiteSettings()): SiteSettings {
  const candidate = value && typeof value === "object" ? value as Partial<Record<keyof SiteSettings, unknown>> : {};
  return {
    societyName: text(candidate.societyName, 120) || fallback.societyName,
    description: text(candidate.description, 500) || fallback.description,
    city: text(candidate.city, 120) || fallback.city,
    locationUrl: text(candidate.locationUrl, 500) || fallback.locationUrl,
    instagram: optionalText(candidate.instagram, fallback.instagram, 500),
    facebook: optionalText(candidate.facebook, fallback.facebook, 500),
    whatsapp: optionalText(candidate.whatsapp, fallback.whatsapp, 500),
  };
}

export async function getSiteSettings(db: Pool): Promise<SiteSettings> {
  const [rows] = await db.query<SiteSettingsRow[]>("SELECT society_name, description, city, location_url, instagram_url, facebook_url, whatsapp_url FROM site_settings WHERE id = 1 LIMIT 1");
  const row = rows[0];
  return normalizeSiteSettings({ societyName: row?.society_name, description: row?.description, city: row?.city, locationUrl: row?.location_url, instagram: row?.instagram_url, facebook: row?.facebook_url, whatsapp: row?.whatsapp_url });
}

export async function saveSiteSettings(db: Pool, value: unknown): Promise<SiteSettings> {
  const settings = normalizeSiteSettings(value, await getSiteSettings(db));
  await db.query("INSERT INTO site_settings (id, society_name, description, city, location_url, instagram_url, facebook_url, whatsapp_url) VALUES (1, ?, ?, ?, ?, ?, ?, ?) ON DUPLICATE KEY UPDATE society_name = VALUES(society_name), description = VALUES(description), city = VALUES(city), location_url = VALUES(location_url), instagram_url = VALUES(instagram_url), facebook_url = VALUES(facebook_url), whatsapp_url = VALUES(whatsapp_url)", [settings.societyName, settings.description, settings.city, settings.locationUrl, settings.instagram, settings.facebook, settings.whatsapp]);
  return settings;
}
